import { useEffect, useState } from "react";
import { useApp } from "../store";
import { ErrorNote, Note } from "../ui";

/**
 * Incoming and active call screens.
 *
 * Shown as an overlay over the phone frame rather than as a route, because a call is a state the user is
 * in, not a place they navigated to - and it has to appear wherever they happen to be.
 *
 * Media is not negotiated: the design defers call signalling implementation, so this screen drives the
 * provider-independent behaviour (ring, answer-and-cancel, background, end) and stops at the audio path.
 */
export function CallOverlay(): JSX.Element | null {
  const { liveCall, answerCall, declineCall, hangUpCall } = useApp();
  const [minimized, setMinimized] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [seconds, setSeconds] = useState(0);

  useEffect(() => {
    if (!liveCall) {
      setMinimized(false);
      setSeconds(0);
      return;
    }
    const timer = window.setInterval(
      () => setSeconds(Math.floor((Date.now() - liveCall.startedAt) / 1000)),
      1000
    );
    return () => window.clearInterval(timer);
  }, [liveCall]);

  if (!liveCall) {
    return null;
  }

  if (minimized) {
    return (
      <button type="button" className="callbar" onClick={() => setMinimized(false)}>
        <span className="ic">{"\u260E"}</span>
        <span className="grow">Ongoing call {"\u2014"} {liveCall.counterpartyLabel}</span>
        <span className="timer">{formatTimer(seconds)}</span>
      </button>
    );
  }

  const ringing = liveCall.state === "ringing";

  return (
    <div className="call-overlay">
      <header className="app-header">
        {!ringing ? (
          <button className="link" type="button" onClick={() => setMinimized(true)}>
            {"\u2193"} Minimize
          </button>
        ) : null}
        <h1>{ringing ? "Incoming call" : "In call"}</h1>
      </header>
      <div className="content">
        <div className="avatar" style={{ width: 96, height: 96, fontSize: 34 }}>
          {liveCall.counterpartyLabel.startsWith("+")
            ? liveCall.counterpartyLabel.slice(0, 3)
            : liveCall.counterpartyLabel
                .split(/\s+/)
                .map((part) => part[0])
                .join("")
                .slice(0, 2)
                .toUpperCase()}
        </div>
        <div className="row-title" style={{ fontSize: 24, marginTop: 16 }}>
          {liveCall.counterpartyLabel}
        </div>
        <div className="row-sub">
          {ringing ? `to ${liveCall.number}` : `on ${liveCall.number} \u00B7 ${formatTimer(seconds)}`}
        </div>
        <ErrorNote error={error} />
        <div style={{ width: "100%", marginTop: 24 }}>
          {ringing ? (
            <>
              <button
                className="btn primary"
                type="button"
                onClick={() => answerCall(liveCall.callSessionId).catch(setError)}
              >
                Answer
              </button>
              <button
                className="btn danger"
                type="button"
                onClick={() => declineCall(liveCall.callSessionId).catch(setError)}
              >
                Decline
              </button>
            </>
          ) : (
            <button
              className="btn danger"
              type="button"
              onClick={() => hangUpCall(liveCall.callSessionId).catch(setError)}
            >
              End call
            </button>
          )}
        </div>
        <Note>
          {ringing
            ? "Ringing on all online devices. First to answer wins; the others stop ringing."
            : "Minimize to use other pages while the call continues."}
        </Note>
      </div>
    </div>
  );
}

/** A short-lived notice about how the last call ended, so a missed call is visible immediately. */
export function CallEndedNotice(): JSX.Element | null {
  const { callEnded, dismissEndedCall } = useApp();

  useEffect(() => {
    if (callEnded) {
      const timer = window.setTimeout(dismissEndedCall, 4000);
      return () => window.clearTimeout(timer);
    }
  }, [callEnded, dismissEndedCall]);

  if (!callEnded) {
    return null;
  }
  return <div className="info">Call {callEnded.replace(/-/g, " ")}</div>;
}

function formatTimer(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  return `${String(minutes).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;
}
